import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCamera, FaUser, FaEnvelope, FaPhone } from "react-icons/fa";
import { motion } from "framer-motion";
import { updateProfileApi } from "../../apis/payments.api";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useApiErrorHandler } from "../../hooks/useApiErrorHandler";

const emptyAccount = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  profileImage: "/Images/user-profile.png",
};

const EditProfile = () => {
  const [account, setAccount] = useState(emptyAccount);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();

  useEffect(() => {
    const loadProfile = () => {
      setLoading(true);

      // Load from localStorage
      const stored = localStorage.getItem("account");
      if (stored && stored !== "undefined" && stored !== "null") {
        try {
          const parsed = JSON.parse(stored);
          setAccount({ ...emptyAccount, ...parsed });
        } catch (error) {
          // If parsing fails, try to get from individual localStorage items
          const userName = localStorage.getItem("userName") || "";
          const userEmail = localStorage.getItem("userEmail") || "";
          const [first, ...rest] = (userName || "").split(" ");
          const last = rest.join(" ");
          setAccount({
            ...emptyAccount,
            firstname: first,
            lastname: last,
            email: userEmail,
          });
        }
      } else {
        // Fallback to individual localStorage items
        const userName = localStorage.getItem("userName") || "";
        const userEmail = localStorage.getItem("userEmail") || "";
        const [first, ...rest] = (userName || "").split(" ");
        const last = rest.join(" ");
        setAccount({
          ...emptyAccount,
          firstname: first,
          lastname: last,
          email: userEmail,
        });
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAccount((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setAccount((prev) => ({ ...prev, profileImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      // Update profile on backend
      const res = await updateProfileApi({
        firstname: account.firstname,
        lastname: account.lastname,
        email: account.email,
        // Note: phone field not supported by backend yet
      });

      if (res.status === 200) {
        // Update localStorage for other components - include profile image
        const fullName = `${account.firstname} ${account.lastname}`.trim();
        const accountData = {
          firstname: account.firstname,
          lastname: account.lastname,
          email: account.email,
          profileImage: account.profileImage, // Include the profile image
          name: fullName,
        };
        localStorage.setItem("account", JSON.stringify(accountData));
        localStorage.setItem("userName", fullName);
        localStorage.setItem("userEmail", account.email);

        // Dispatch custom event to notify other components
        window.dispatchEvent(
          new CustomEvent("profileUpdated", { detail: accountData }),
        );

        alert("✅ Profile Updated Successfully");
        navigate("/dashboard/profile");
      }
    } catch (err) {
      // console.error('Error updating profile:', err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to update profile";
      setError(errorMsg);
      alert("❌ " + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 ${
        isDarkMode ? "bg-gray-950" : "bg-gray-50"
      }`}
    >
      <div className="max-w-2xl mx-auto">
        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 rounded-lg p-4 border-l-4 ${
              isDarkMode
                ? "bg-red-900/30 border-red-500"
                : "bg-red-50 border-red-500"
            }`}
          >
            <p
              className={`font-medium flex items-center gap-2 ${
                isDarkMode ? "text-red-300" : "text-red-700"
              }`}
            >
              <span className="text-xl">⚠️</span>
              {error}
            </p>
          </motion.div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div
              className={`w-12 h-12 border-4 rounded-full animate-spin mb-4 ${
                isDarkMode
                  ? "border-gray-700 border-t-[#008300]"
                  : "border-gray-300 border-t-[#008300]"
              }`}
            ></div>
            <p
              className={`text-lg font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              Loading profile...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10 text-center sm:text-left"
            >
              <h1
                className={`text-4xl sm:text-5xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Edit Profile
              </h1>
              <p
                className={`text-base sm:text-lg ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Update your personal information and profile picture
              </p>
            </motion.div>

            {/* Main Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className={`rounded-2xl p-6 sm:p-10 border-2 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Profile Picture Section */}
              <div className="mb-10">
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-[#008300] shadow-xl group mb-4">
                    <img
                      src={account.profileImage || "/Images/user-profile.png"}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                    <label className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer">
                      <div className="flex flex-col items-center gap-2 text-white">
                        <FaCamera size={28} />
                        <span className="text-xs font-medium">
                          Change Photo
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                  <p
                    className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Hover to change profile picture
                  </p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleUpdate} className="space-y-6">
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label
                      className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      First Name
                    </label>
                    <div className="relative">
                      <FaUser
                        className={`absolute left-4 top-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        size={18}
                      />
                      <input
                        name="firstname"
                        value={account.firstname}
                        onChange={handleChange}
                        placeholder="John"
                        className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#008300] focus:border-transparent transition-all ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                            : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Last Name
                    </label>
                    <div className="relative">
                      <FaUser
                        className={`absolute left-4 top-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        size={18}
                      />
                      <input
                        name="lastname"
                        value={account.lastname}
                        onChange={handleChange}
                        placeholder="Doe"
                        className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#008300] focus:border-transparent transition-all ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                            : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Email - Read Only */}
                <div>
                  <label
                    className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope
                      className={`absolute left-4 top-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      size={18}
                    />
                    <input
                      type="email"
                      name="email"
                      value={account.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#008300] focus:border-transparent transition-all ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                          : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label
                    className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Contact Number
                  </label>
                  <div className="relative">
                    <FaPhone
                      className={`absolute left-4 top-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      size={18}
                    />
                    <input
                      name="phone"
                      value={account.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-[#008300] focus:border-transparent transition-all ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                          : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={saving}
                    className={`flex-1 py-3 sm:py-4 px-6 rounded-lg font-bold text-base transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                      saving
                        ? isDarkMode
                          ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : isDarkMode
                          ? "bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white"
                          : "bg-gradient-to-r from-[#008300] to-green-600 hover:from-[#006400] hover:to-[#008300] text-white"
                    }`}
                  >
                    {saving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Updating...
                      </>
                    ) : (
                      <>💾 Save Changes</>
                    )}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => navigate(-1)}
                    className={`flex-1 py-3 sm:py-4 px-6 rounded-lg font-bold text-base transition-all duration-200 border-2 ${
                      isDarkMode
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    ← Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>

            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`mt-8 p-6 rounded-lg border-l-4 ${
                isDarkMode
                  ? "bg-gray-800 border-l-[#008300]"
                  : "bg-blue-50 border-l-blue-500"
              }`}
            >
              <p
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                <span className="font-bold block mb-2">
                  💡 Profile Information
                </span>
                Your profile information helps us provide better personalized
                services. Your data is always secure and private.
              </p>
            </motion.div>
          </>
        )}
      </div>
    </div></>
  );
};

export default EditProfile;
